import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Camera, LayoutTemplate, Plus, Sparkles, Mountain, Star, X, Video } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ChatMessage, EditThumb } from "@/components/creatives/ChatMessage";
import { PublishDialog } from "@/components/creatives/PublishDialog";
import { ModelPicker } from "@/components/creatives/ModelPicker";
import { MusicDialog } from "@/components/creatives/MusicDialog";
import { ScenesDialog } from "@/components/creatives/ScenesDialog";
import { ReviewDialog } from "@/components/creatives/ReviewDialog";
import { TextVideoDialog } from "@/components/creatives/TextVideoDialog";
import { DirectVideoDialog } from "@/components/creatives/DirectVideoDialog";
import { TemplatesMenu, SaveTemplateDialog } from "@/components/creatives/Templates";
import { BannerPicker } from "@/components/creatives/BannerPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;
const FORMATS = [["1:1", "Pătrat 1:1"], ["4:5", "Feed 4:5"], ["9:16", "Story 9:16"]];
const EST = { "nb-pro": 0.15, "nb-2": 0.12, seedream: 0.04, "gpt-2": 0.25 };
const QUICK = [
  { id: "lifestyle", label: "Foto realist", icon: Camera, text: "Fă o fotografie realistă de reclamă cu produsul folosit într-o casă modernă, lumină naturală, produsul în prim-plan." },
  { id: "graphic", label: "Grafic reclamă", icon: LayoutTemplate, text: "Fă un grafic de reclamă cu fundal colorat. Titlu: „…”. Preț: „… lei”. Buton: „Comandă acum”." },
];

const EditChip = ({ a, onClear }) => (
  <div data-testid="edit-chip" className="flex items-center gap-3 rounded-lg border border-amber-400/50 bg-amber-400/5 p-2">
    <EditThumb url={a.url} isVideo={a.kind === "video"} className="h-12 w-12 rounded-md" />
    <p className="flex-1 text-xs text-amber-100">
      {a.kind === "video" ? "Refac acest video (de la aceeași poză de start) după ce scrii mai jos — generare nouă, ~2–5 min." : "Modific exact această poză — scrie mai jos ce schimb."}
    </p>
    <button data-testid="edit-chip-clear" onClick={onClear} title="Renunță" className="rounded-full p-1 text-zinc-400 hover:text-rose-400"><X className="h-4 w-4" /></button>
  </div>
);

export const ChatPanel = ({ product, selected, onChanged, editAsset, setEditAsset }) => {
  const [sessions, setSessions] = useState([]);
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  const [n, setN] = useState(1);
  const [busy, setBusy] = useState(false);
  const [ars, setArs] = useState(["1:1"]);
  const ar = ars[0];
  const setAr = (k) => setArs([k]);
  const toggleAr = (k) => setArs((cur) => {
    const fmt = cur.filter((x) => FORMATS.some(([f]) => f === x));
    const next = fmt.includes(k) ? fmt.filter((x) => x !== k) : [...fmt, k];
    return next.length ? next : cur;
  });
  const [models, setModels] = useState(["nb-pro"]);
  const [heads, setHeads] = useState(null);
  const [headBusy, setHeadBusy] = useState(false);
  const [pub, setPub] = useState(null);
  const [mus, setMus] = useState(null);
  const [setBusyAB, setSetBusyAB] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [txt, setTxt] = useState(null);
  const [tplDraft, setTplDraft] = useState(null);
  const [tplKey, setTplKey] = useState(0);
  const boxRef = useRef(null);

  const loadList = () => axios.get(`${API}/sessions`).then((r) => setSessions(r.data.items || [])).catch(() => {});
  const open = (id) => axios.get(`${API}/sessions/${id}`).then((r) => setS(r.data)).catch((e) => toast.error(e.message));
  useEffect(() => { loadList(); }, []);
  useEffect(() => { const b = boxRef.current; if (b) b.scrollTo({ top: b.scrollHeight, behavior: "smooth" }); }, [s?.messages?.length, heads]);

  const running = s?.messages?.some((m) => m.status === "running");
  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => {
      const r = await axios.get(`${API}/sessions/${s.id}`);
      setS(r.data);
      if (!r.data.messages.some((m) => m.status === "running")) onChanged();
    }, 4000);
    return () => clearInterval(t);
  }, [running, s?.id]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sessions/message`, { session_id: s?.id, text, num_images: n, aspect_ratio: editAsset?.aspect_ratio || ar, aspect_ratios: editAsset ? [] : ars, models, source_urls: selected, sku: product?.sku, product_title: product?.title, edit_asset_id: editAsset?.id }, { timeout: 90000 });
      setS(r.data); setText(""); setHeads(null); setEditAsset(null); loadList(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const pick = async (id) => { await axios.post(`${API}/sessions/${s.id}/current`, { asset_id: id }); setS({ ...s, current_image_id: id }); };
  const removeAsset = async (a) => {
    if (!window.confirm("Ștergi creativul?")) return;
    try { await axios.delete(`${API}/${a.id}`); toast.success("Creativ șters"); open(s.id); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const reformat = async (a, f) => {
    try {
      await axios.post(`${API}/${a.id}/reformat`, { aspect_ratio: f });
      toast.success(`Se reface în ${f}`);
      open(s.id); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const upscale = async (a) => {
    try { await axios.post(`${API}/${a.id}/upscale`); toast.success("Mărire 4K pornită"); open(s.id); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const abSet = async () => {
    setSetBusyAB(true);
    try {
      const r = await axios.post(`${API}/sessions/set`, { session_id: s?.id, source_urls: selected, sku: product?.sku || s?.sku, aspect_ratio: ar, models, text }, { timeout: 90000 });
      setS(r.data); loadList(); onChanged(); toast.success("Set A/B pornit — 3 creative în ~1 minut");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSetBusyAB(false);
  };
  const suggest = async () => {
    const sku = product?.sku || s?.sku;
    if (!sku) return toast.error("Alege mai întâi un produs");
    setHeadBusy(true);
    try { const r = await axios.post(`${API}/headlines`, { sku }); setHeads(r.data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setHeadBusy(false);
  };
  const batchBody = { session_id: s?.id, source_urls: selected, sku: product?.sku || s?.sku, product_title: product?.title || s?.product_title, aspect_ratio: ar, models };
  const openBatch = (kind) => {
    if (kind === "review" && !s && !selected.length) return toast.error("Selectează cel puțin o poză a produsului");
    setDlg(kind);
  };
  const batchDone = (data) => { setS(data); loadList(); onChanged(); };
  const saveTemplate = (m) => {
    const idx = s.messages.findIndex((x) => x.id === m.id);
    const text = s.messages.slice(0, idx).filter((x) => x.role === "user").map((x) => x.text).join("\n");
    setTplDraft({ text, aspect_ratio: m.assets?.[0]?.aspect_ratio || ar, models: m.model ? [m.model] : models });
  };
  const applyTemplate = (t) => { setText(t.text); setAr(t.aspect_ratio); setModels(t.models); toast.success(`Șablon „${t.name}” pus în mesaj — alege produsul și trimite`); };
  const useHeadline = (h) => setText((t) => `${t ? t.trim() + " " : ""}Titlu: „${h}”`);

  return (
    <div className="flex h-[720px] flex-col rounded-xl border border-zinc-800 bg-zinc-900/50" data-testid="chat-panel">
      <div className="flex items-center gap-2 border-b border-zinc-800 p-3">
        <h2 className="text-base font-semibold text-zinc-100">2. Spune ce vrei</h2>
        <select data-testid="session-select" value={s?.id || ""} onChange={(e) => { setHeads(null); e.target.value ? open(e.target.value) : setS(null); }} className="ml-auto max-w-[200px] truncate rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-300">
          <option value="">Conversație nouă</option>
          {sessions.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
        </select>
        <button data-testid="new-session-btn" onClick={() => { setS(null); setHeads(null); }} title="Conversație nouă" className="rounded-full border border-zinc-700 p-1.5 text-zinc-300 hover:border-amber-400"><Plus className="h-3.5 w-3.5" /></button>
      </div>
      <div ref={boxRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="chat-messages">
        {!s && <p className="text-sm text-zinc-500">Alege pozele din stânga și scrie în română ce reclamă vrei. Apoi cere corecturi („pune fundal roșu”, „mărește prețul”) sau „fă un video de 5 secunde în care iese abur”. Click pe o imagine ca s-o alegi drept versiunea de la care continui.</p>}
        {s?.messages?.map((m) => <ChatMessage key={m.id} m={m} currentId={s.current_image_id} onPick={pick} onReformat={reformat} onPublish={setPub} onUseHeadline={useHeadline} onUpscale={upscale} onMusic={setMus} onText={setTxt} onSaveTemplate={saveTemplate} onEdit={setEditAsset} onDelete={removeAsset} />)}
        {heads && <ChatMessage m={{ id: "headlines", role: "assistant", text: "5 titluri din descrierea produsului — click pe unul ca să-l adaugi în mesaj:", headlines: heads }} onUseHeadline={useHeadline} />}
      </div>
      {pub && <PublishDialog asset={pub} onClose={() => setPub(null)} onDone={() => s && open(s.id)} />}
      {mus && <MusicDialog asset={mus} onClose={() => setMus(null)} onStarted={() => { s && open(s.id); onChanged(); }} />}
      {txt && <TextVideoDialog asset={txt} onClose={() => setTxt(null)} onStarted={() => { s && open(s.id); onChanged(); }} />}
      {dlg === "video" && <DirectVideoDialog photo={selected[0]} product={product} initialText={text} onClose={() => setDlg(null)} onStarted={onChanged} />}
      {dlg === "scenes" && <ScenesDialog body={batchBody} estCost={EST[models[0]] || 0.15} onClose={() => setDlg(null)} onDone={batchDone} />}
      {dlg === "review" && <ReviewDialog body={batchBody} estCost={EST[models[0]] || 0.15} onClose={() => setDlg(null)} onDone={batchDone} />}
      {tplDraft && <SaveTemplateDialog draft={tplDraft} onClose={() => setTplDraft(null)} onSaved={() => setTplKey((k) => k + 1)} />}
      <div className="space-y-2 border-t border-zinc-800 p-3">
        <ModelPicker value={models} onChange={setModels} ar={ar === "banner-both" ? "banner-desktop" : ar} n={ar === "banner-both" ? n * 2 : n * ars.length} />
        <div className="flex flex-wrap items-center gap-2">
          {FORMATS.map(([k, l]) => (
            <button key={k} data-testid={`format-${k.replace(":", "x")}`} onClick={() => toggleAr(k)} title="Poți bifa mai multe dimensiuni; apasă iar ca să o debifezi" className={`rounded-full border px-2.5 py-0.5 text-xs ${ars.includes(k) ? "border-amber-400 bg-amber-400 text-zinc-950" : "border-zinc-700 text-zinc-400"}`}>{l}</button>
          ))}
          <BannerPicker ar={ar} setAr={setAr} />
          <button data-testid="ab-set-btn" onClick={abSet} disabled={setBusyAB} title="3 creative diferite: foto realist, grafic cu preț, grafic cu beneficii" className="ml-auto flex items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-400/10 px-3 py-0.5 text-xs text-amber-100 hover:bg-amber-400 hover:text-zinc-950">
            {setBusyAB ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LayoutTemplate className="h-3.5 w-3.5" />} Set A/B (3 creative)
          </button>
          <button data-testid="direct-video-btn" onClick={() => setDlg("video")} title="Video direct din poza selectată sau doar din text" className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-0.5 text-xs text-zinc-300 hover:border-amber-400">
            <Video className="h-3.5 w-3.5 text-amber-400" /> Video
          </button>
          <button data-testid="scenes-btn" onClick={() => openBatch("scenes")} title="Produsul din pozele tale, în bucătărie, birou, mașină…" className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-0.5 text-xs text-zinc-300 hover:border-amber-400">
            <Mountain className="h-3.5 w-3.5 text-amber-400" /> Scene noi
          </button>
          <button data-testid="review-btn" onClick={() => openBatch("review")} title="Reclamă cu o recenzie reală de 5 stele" className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-0.5 text-xs text-zinc-300 hover:border-amber-400">
            <Star className="h-3.5 w-3.5 text-amber-400" /> Din recenzie
          </button>
          <TemplatesMenu refreshKey={tplKey} onApply={applyTemplate} />
          <button data-testid="suggest-headlines-btn" onClick={suggest} disabled={headBusy} className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-0.5 text-xs text-zinc-300 hover:border-amber-400">
            {headBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-amber-400" />} 5 titluri AI
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {QUICK.map((q) => (
            <button key={q.id} data-testid={`quick-${q.id}`} onClick={() => setText(q.text)} className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-300 hover:border-amber-400"><q.icon className="h-3.5 w-3.5 text-amber-400" /> {q.label}</button>
          ))}
          <span className="ml-auto text-xs text-zinc-500">Variante:</span>
          {[1, 2, 3, 4].map((k) => (
            <button key={k} data-testid={`num-images-${k}`} onClick={() => setN(k)} className={`h-6 w-6 rounded-full border text-xs ${n === k ? "border-amber-400 bg-amber-400 text-zinc-950" : "border-zinc-700 text-zinc-400"}`}>{k}</button>
          ))}
        </div>
        {editAsset && <EditChip a={editAsset} onClear={() => setEditAsset(null)} />}
        <div className="flex gap-2">
          <Textarea data-testid="chat-input" autoFocus={!!editAsset} key={editAsset?.id || "free"} value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={editAsset ? (editAsset.kind === "video" ? "Ce schimb la video? ex: „mișcare mai lentă, abur mai mult”" : "Ce schimb la poză? ex: „fundal roșu, preț mai mare”") : "Scrie exact ce vrei… (merge și fără produs selectat)"}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
          <button data-testid="chat-send-btn" onClick={send} disabled={busy} className="flex w-12 items-center justify-center rounded-xl bg-amber-400 text-zinc-950 transition-transform hover:scale-105 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};
